import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Ticket,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  MapPin,
  Calendar,
  TrendingUp,
  MessageSquare,
  Star,
  Activity,
  Zap,
  Heart,
  Award,
  Target,
  Sparkles,
  Crown,
  Shield,
  Megaphone,
  X,
  Bell,
  Bot,
  ShoppingBag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useTickets } from '../../contexts/TicketContext';
import { usePunishment } from '../../contexts/PunishmentContext';
import { getStatusColor } from '../../lib/theme';
import { useUnreadAnnouncements } from '../../hooks/useUnreadAnnouncements';
import NotificationBell from './NotificationBell';
import { useDynamicGradient } from '../../hooks/useDynamicGradient';

/**
 * Dashboard do Usuário Moderno e Redesenhado
 * Layout moderno com visual atrativo e funcionalidades aprimoradas
 */
const UserDashboard = () => {
  const { user } = useAuth();
  const { tickets, loading, getTicketStats } = useTickets();
  const navigate = useNavigate();
  const { unreadCount, hasUnread } = useUnreadAnnouncements();
  const [recentTickets, setRecentTickets] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dismissedNotification, setDismissedNotification] = useState(false);
  const dynamicGradient = useDynamicGradient();

  // Relógio em tempo real para exibição
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Carrega tickets recentes do usuário
  useEffect(() => {
    if (tickets.length > 0) {
      const userTickets = tickets
        .filter(ticket => ticket.userId === user.id)
        .sort((a, b) => new Date(b.lastUpdate) - new Date(a.lastUpdate))
        .slice(0, 3); // Reduzido para 3 para layout mais limpo
      setRecentTickets(userTickets);
    }
  }, [tickets, user.id]);

  // Punição Notification Logic
  const { getActivePunishments } = usePunishment();
  const [activePunishment, setActivePunishment] = useState(null);

  useEffect(() => {
    const allActive = getActivePunishments(user.discordId);
    const notificationTypes = ['Amarração', 'Intimação', 'Alinhamento', 'Solicitar Contato'];
    const punishments = allActive.filter(p => !notificationTypes.includes(p.punishmentType));

    if (punishments && punishments.length > 0) {
      // Prioritize pending_contact or active over others
      const priorityPunishment = punishments.find(p => p.status === 'pending_contact') || punishments[0];

      // Check if dismissed locally for this specific punishment ID
      const dismissedId = localStorage.getItem('min_dismissed_punishment');
      if (dismissedId !== priorityPunishment.id) {
        setActivePunishment(priorityPunishment);
      }
    }
  }, [user.id, getActivePunishments]);

  const handleDismissPunishment = () => {
    if (activePunishment) {
      localStorage.setItem('min_dismissed_punishment', activePunishment.id);
      setActivePunishment(null);
    }
  };

  const handleAppealRedirect = () => {
    handleDismissPunishment(); // Dismiss on action as requested "Após clicar em Recorrer... a mensagem some"

    if (activePunishment?.status === 'pending_contact' && activePunishment.ticketId) {
      navigate(`/tickets/${activePunishment.ticketId}`);
      return;
    }

    navigate('/characters');
  };

  // Estatísticas dos tickets do usuário
  // Estatísticas dos tickets do usuário (Dual-Layer)
  const userStats = {
    total: tickets.filter(t => t.userId === user.id).length,

    // "Em Aberto" -> Interpretado como "Aguardando resposta da Staff" ou "Verificando"
    open: tickets.filter(t =>
      t.userId === user.id &&
      (
        (Array.isArray(t.auxiliaryStatus) && t.auxiliaryStatus.includes('aguardando_staff')) ||
        t.status === 'aguardando_sistema'
      )
    ).length,

    // "Em Andamento" -> Interpretado como "(Aguardando sua resposta)"
    inProgress: tickets.filter(t =>
      t.userId === user.id &&
      Array.isArray(t.auxiliaryStatus) &&
      t.auxiliaryStatus.includes('aguardando_usuario')
    ).length,

    // "Resolvidos" -> Resolvido (Aux) ou Fechado (Structural)
    resolved: tickets.filter(t =>
      t.userId === user.id &&
      (
        (Array.isArray(t.auxiliaryStatus) && t.auxiliaryStatus.includes('resolvido')) ||
        t.status === 'fechado'
      )
    ).length
  };

  // Função para formatar data/hora em português brasileiro
  const formatDateTime = (date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo'
    }).format(date);
  };

  // Greeting Logic with Memoization
  const greeting = React.useMemo(() => {
    const hour = currentTime.getHours();

    const greetings = {
      dawn: [{ text: 'Boa madrugada', emoji: '🦉' }, { text: 'Silêncio da noite', emoji: '🌌' }],
      morning: [{ text: 'Bom dia', emoji: '🌅' }, { text: 'Ótimo dia', emoji: '☀️' }, { text: 'Energia total', emoji: '⚡' }],
      lunch: [{ text: 'Bom almoço', emoji: '🍽️' }, { text: 'Tenha um bom apetite', emoji: '🥗' }],
      afternoon: [{ text: 'Boa tarde', emoji: '☀️' }, { text: 'Tarde produtiva', emoji: '🚀' }],
      lateAfternoon: [{ text: 'Bom fim de tarde', emoji: '🌇' }],
      night: [{ text: 'Boa noite', emoji: '🌙' }, { text: 'Bom descanso', emoji: '💤' }]
    };

    let timeOfDay;
    if (hour >= 0 && hour < 6) timeOfDay = 'dawn';
    else if (hour >= 6 && hour < 12) timeOfDay = 'morning';
    else if (hour >= 12 && hour < 14) timeOfDay = 'lunch';
    else if (hour >= 14 && hour < 18) timeOfDay = 'afternoon';
    else if (hour === 18) timeOfDay = 'lateAfternoon';
    else timeOfDay = 'night';

    // Pick a random greeting from the appropriate array
    const options = greetings[timeOfDay];
    const selected = options[Math.floor(Math.random() * options.length)];

    return {
      ...selected,
    };
  }, [currentTime.getHours()]); // Only updates when the hour changes

  // Função para navegar para Meus Tickets
  const handleViewMyTickets = () => {
    navigate('/tickets');
  };

  // Função para navegar para criar ticket
  const handleCreateTicket = () => {
    navigate('/tickets/create');
  };

  // Função para navegar para configurações
  const handleSettings = () => {
    navigate('/settings');
  };

  // Função para abrir a loja oficial
  const handleVisitStore = () => {
    window.open('https://capitalcity.tebex.io', '_blank');
  };

  // Função para formatar data
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="w-full min-h-full bg-gradient-to-br from-black via-gray-900 to-blue-900 animate-gradient-slow text-white p-6 rounded-3xl">
      {/* Header Moderno com Saudação e Relógio */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-4xl font-bold">
                <span className={`bg-gradient-to-r ${dynamicGradient} bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-x`}>
                  {greeting.text}, {user?.name || 'Usuário'}!
                </span>
                <span className="ml-2 text-white inline-block hover:scale-110 transition-transform">
                  {greeting.emoji}
                </span>
              </h1>
              <p className="text-gray-400 mt-1 text-lg">
                Bem-vindo ao seu painel de controle
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">


            <div className="text-right bg-gradient-to-r from-black/40 to-black/20 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
              <p className="text-sm text-gray-400">Agora</p>
              <p className="text-white font-mono text-lg font-bold">
                {currentTime.toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'America/Sao_Paulo'
                })}
              </p>
              <p className="text-xs text-gray-500">
                {formatDateTime(currentTime).split(',')[0]}
              </p>
            </div>
          </div>
        </div>

        {/* Notificação de Punição Ativa */}
        {activePunishment && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8"
          >
            <Card className={`${activePunishment.status === 'under_review' ? 'bg-orange-500/10 border-orange-500/30' : activePunishment.status === 'pending_contact' ? 'bg-blue-500/10 border-blue-500/30' : 'bg-red-500/10 border-red-500/30'} overflow-hidden relative`}>
              <div className={`absolute inset-0 ${activePunishment.status === 'under_review' ? 'bg-orange-500/5' : activePunishment.status === 'pending_contact' ? 'bg-blue-500/5' : 'bg-red-500/5'} animate-pulse`} />
              <CardContent className="p-6 relative z-10 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`p-3 ${activePunishment.status === 'under_review' ? 'bg-orange-500/20 border-orange-500/30' : activePunishment.status === 'pending_contact' ? 'bg-blue-500/20 border-blue-500/30' : 'bg-red-500/20 border-red-500/30'} rounded-full border shrink-0`}>
                    {activePunishment.status === 'pending_contact' ? (
                      <MessageSquare className="w-8 h-8 text-blue-500" />
                    ) : (
                      <AlertCircle className={`w-8 h-8 ${activePunishment.status === 'under_review' ? 'text-orange-500' : 'text-red-500'}`} />
                    )}
                  </div>


                  <div>
                    <h3 className={`text-lg font-bold ${activePunishment.status === 'under_review' ? 'text-orange-100' : activePunishment.status === 'pending_contact' ? 'text-blue-100' : 'text-red-100'} flex items-center gap-2`}>
                      {activePunishment.status === 'under_review' ? 'Atenção! Punição em Análise' : activePunishment.status === 'pending_contact' ? 'Ação Necessária' : 'Atenção! Punição Ativa Detectada'}
                      <Badge variant="outline" className={`${activePunishment.status === 'under_review' ? 'border-orange-500/50 text-orange-400 bg-orange-500/10' : activePunishment.status === 'pending_contact' ? 'border-blue-500/50 text-blue-400 bg-blue-500/10' : 'border-red-500/50 text-red-400 bg-red-500/10'} text-xs`}>
                        {activePunishment.typeLabel || activePunishment.punishmentType}
                      </Badge>
                    </h3>
                    <p className={`${activePunishment.status === 'under_review' ? 'text-orange-200/80' : activePunishment.status === 'pending_contact' ? 'text-blue-200/80' : 'text-red-200/80'} text-sm mt-1`}>
                      {activePunishment.status === 'under_review'
                        ? `Sua punição (${activePunishment.characterName}) está sendo analisada pela equipe. Você será notificado sobre a decisão.`
                        : activePunishment.status === 'pending_contact'
                          ? `A Staff solicitou um contato referente ao personagem ${activePunishment.characterName}. Por favor, verifique o ticket criado.`
                          : activePunishment.punishmentType === 'Blacklist de Tickets'
                            ? "Detectamos uma punição ativa em sua conta. Você pode verificar os detalhes e recorrer da decisão."
                            : `Detectamos uma punição ativa em sua conta (${activePunishment.characterName}). Você pode verificar os detalhes e recorrer da decisão.`
                      }
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 w-full md:w-auto">
                  <Button
                    onClick={handleDismissPunishment}
                    variant="ghost"
                    className="text-red-300 hover:text-red-200 hover:bg-red-500/20"
                  >
                    Fechar
                  </Button>
                  <Button
                    onClick={handleAppealRedirect}
                    className={`${activePunishment.status === 'pending_contact' ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-900/20' : 'bg-red-600 hover:bg-red-700 shadow-red-900/20'} text-white border-none shadow-lg whitespace-nowrap flex-1 md:flex-none`}
                  >
                    {activePunishment.status === 'pending_contact' ? 'Ver Solicitação' : 'Recorrer da Decisão'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Notificação de Comunicados Não Lidos */}
        {hasUnread && !dismissedNotification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 cursor-pointer"
            onClick={() => navigate('/announcements')}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-pink-600/10 to-purple-600/10 rounded-2xl blur-2xl" />
            <Card className="relative bg-gradient-to-r from-purple-900/40 to-pink-900/40 backdrop-blur-lg border border-purple-500/50 hover:border-purple-400/70 transition-all">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl animate-pulse">
                      <Megaphone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        ⚠️ Atenção! Você tem {unreadCount} novo{unreadCount !== 1 ? 's' : ''} aviso{unreadCount !== 1 ? 's' : ''} não lido{unreadCount !== 1 ? 's' : ''}
                      </h3>
                      <p className="text-purple-200 text-sm">
                        Clique aqui para visualizar
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDismissedNotification(true);
                      }}
                      className="text-purple-200 hover:text-white hover:bg-purple-800/30"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Cards de Estatísticas Modernos */}
        {/* Cards de Estatísticas Modernos */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ scale: 1.05, y: -5 }}
          >
            <Card className="relative overflow-hidden bg-white/5 border-white/10 hover:border-white/20 backdrop-blur-xl hover:shadow-2xl hover:shadow-black/20 transition-all duration-500 group">
              {/* Efeito de Reflexo Glass */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-50 pointer-events-none" />

              {/* Brilho Dinâmico de Fundo */}
              <div className={`absolute -right-8 -top-8 w-32 h-32 bg-gradient-to-br ${dynamicGradient} blur-3xl opacity-10 group-hover:opacity-30 transition-opacity duration-500`} />

              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium">Total de Tickets</p>
                    <p className={`text-3xl font-bold mt-1 bg-gradient-to-r ${dynamicGradient} bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-x`}>{userStats.total}</p>
                    <p className="text-xs text-gray-500 mt-1">Todos os tempos</p>
                  </div>
                  <div className={`p-3 bg-gradient-to-br ${dynamicGradient} opacity-80 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <Ticket className="h-8 w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ scale: 1.05, y: -5 }}
          >
            <Card className="relative overflow-hidden bg-white/5 border-white/10 hover:border-white/20 backdrop-blur-xl hover:shadow-2xl hover:shadow-black/20 transition-all duration-500 group">
              {/* Efeito de Reflexo Glass */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-50 pointer-events-none" />

              {/* Brilho Dinâmico de Fundo */}
              <div className={`absolute -right-8 -top-8 w-32 h-32 bg-gradient-to-br ${dynamicGradient} blur-3xl opacity-10 group-hover:opacity-30 transition-opacity duration-500`} />

              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium">Em Aberto</p>
                    <p className={`text-3xl font-bold mt-1 bg-gradient-to-r ${dynamicGradient} bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-x`}>{userStats.open}</p>
                    <p className="text-xs text-gray-500 mt-1">Aguardando resposta da Staff</p>
                  </div>
                  <div className={`p-3 bg-gradient-to-br ${dynamicGradient} opacity-80 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <Clock className="h-8 w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.05, y: -5 }}
          >
            <Card className="relative overflow-hidden bg-white/5 border-white/10 hover:border-white/20 backdrop-blur-xl hover:shadow-2xl hover:shadow-black/20 transition-all duration-500 group">
              {/* Efeito de Reflexo Glass */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-50 pointer-events-none" />

              {/* Brilho Dinâmico de Fundo */}
              <div className={`absolute -right-8 -top-8 w-32 h-32 bg-gradient-to-br ${dynamicGradient} blur-3xl opacity-10 group-hover:opacity-30 transition-opacity duration-500`} />

              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium">Em Andamento</p>
                    <p className={`text-3xl font-bold mt-1 bg-gradient-to-r ${dynamicGradient} bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-x`}>{userStats.inProgress}</p>
                    <p className="text-xs text-gray-500 mt-1">(Aguardando sua resposta)</p>
                  </div>
                  <div className={`p-3 bg-gradient-to-br ${dynamicGradient} opacity-80 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <Activity className="h-8 w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ scale: 1.05, y: -5 }}
          >
            <Card className="relative overflow-hidden bg-white/5 border-white/10 hover:border-white/20 backdrop-blur-xl hover:shadow-2xl hover:shadow-black/20 transition-all duration-500 group">
              {/* Efeito de Reflexo Glass */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-50 pointer-events-none" />

              {/* Brilho Dinâmico de Fundo */}
              <div className={`absolute -right-8 -top-8 w-32 h-32 bg-gradient-to-br ${dynamicGradient} blur-3xl opacity-10 group-hover:opacity-30 transition-opacity duration-500`} />

              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium">Resolvidos</p>
                    <p className={`text-3xl font-bold mt-1 bg-gradient-to-r ${dynamicGradient} bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-x`}>{userStats.resolved}</p>
                    <p className="text-xs text-gray-500 mt-1">Concluídos</p>
                  </div>
                  <div className={`p-3 bg-gradient-to-br ${dynamicGradient} opacity-80 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <CheckCircle className="h-8 w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </motion.div>


      {/* Conteúdo Principal Redesenhado */}
      <div className="space-y-8">


        {/* Ações Rápidas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Botão Meus Tickets */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.02 }}
          >
            <Card className="bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 border-blue-500/30 backdrop-blur-sm hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300 h-full">
              <CardContent className="p-8 flex flex-col justify-between h-full">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="p-4 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-2xl">
                    <Ticket className="h-8 w-8 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Meus Tickets</h3>
                    <p className="text-gray-300 mt-1">Gerencie seus chamados</p>
                  </div>
                </div>
                <Button
                  onClick={handleViewMyTickets}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium py-6 text-lg shadow-lg"
                >
                  Ver Tickets
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Botão Criar Ticket */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ scale: 1.02 }}
          >
            <Card className="bg-gradient-to-r from-emerald-600/20 via-teal-600/20 to-green-600/20 border-emerald-500/30 backdrop-blur-sm hover:shadow-2xl hover:shadow-emerald-500/20 transition-all duration-300 h-full">
              <CardContent className="p-8 flex flex-col justify-between h-full">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="p-4 bg-gradient-to-r from-emerald-500/30 to-teal-500/30 rounded-2xl">
                    <Plus className="h-8 w-8 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Criar Ticket</h3>
                    <p className="text-gray-300 mt-1">Novo atendimento</p>
                  </div>
                </div>
                <Button
                  onClick={handleCreateTicket}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium py-6 text-lg shadow-lg"
                >
                  Novo Ticket
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Botão Loja Oficial */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            whileHover={{ scale: 1.02 }}
          >
            <Card className="bg-gradient-to-r from-orange-600/20 via-yellow-600/20 to-amber-600/20 border-orange-500/30 backdrop-blur-sm hover:shadow-2xl hover:shadow-orange-500/20 transition-all duration-300 h-full">
              <CardContent className="p-8 flex flex-col justify-between h-full">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="p-4 bg-gradient-to-r from-orange-500/30 to-yellow-500/30 rounded-2xl">
                    <ShoppingBag className="h-8 w-8 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Nossa Loja</h3>
                    <p className="text-gray-300 mt-1">Visite a loja oficial</p>
                  </div>
                </div>
                <Button
                  onClick={handleVisitStore}
                  className="w-full bg-gradient-to-r from-orange-500 to-yellow-600 hover:from-orange-600 hover:to-yellow-700 text-white font-medium py-6 text-lg shadow-lg"
                >
                  Visitar Loja
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
